"use client";
import { Command } from "cmdk";
import {
  Home,
  Settings,
  Users,
  Calendar,
  Plus,
  HelpCircle,
  Star,
} from "lucide-react";
import { useCommandBar } from "@/components/command-bar/command-bar-context";
import { useCommandBarSearch } from "@/components/command-bar/use-command-bar-search";
import type { CommandGroup } from "@/components/command-bar/command-bar-context";
import { GoConfig, useDataProvider, useGo } from "@refinedev/core";
import { GoConfigWithResource } from "@refinedev/core/dist/hooks/router/use-go";
import _ from "lodash";
import { IEventData } from "@/types/event";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import moment from "moment";
import { cn } from "@/lib/utils";

// Default global shortcuts that are always available
const defaultShortcuts = ({
  go,
}: {
  go: (config: GoConfig | GoConfigWithResource) => string | void;
}): CommandGroup[] => [
  {
    id: "navigation",
    heading: "Navigation",
    actions: [
      {
        id: "home",
        label: "Go to Home",
        description: "Navigate to homepage",
        icon: <Home className="h-4 w-4" />,
        onSelect: () => go({ to: "/home" }),
        keywords: ["home", "main", "dashboard"],
      },
      {
        id: "events",
        label: "View Events",
        description: "Browse all events",
        icon: <Calendar className="h-4 w-4" />,
        onSelect: () => go({ to: "/events" }),
        keywords: ["events", "calendar", "schedule"],
      },
    ],
  },
  {
    id: "actions",
    heading: "Quick Actions",
    actions: [
      {
        id: "new-event",
        label: "Create New Event",
        description: "Add a new event",
        icon: <Plus className="h-4 w-4" />,
        onSelect: () => go({ to: "/events/create" }),
        keywords: ["create", "new", "add", "event"],
      },
      {
        id: "new-venue",
        label: "Create New Venue",
        description: "Add a new venue",
        icon: <Star className="h-4 w-4" />,
        onSelect: () => go({ to: "/venues/create" }),
        keywords: ["create", "new", "add", "venue", "location"],
      },
    ],
  },
  {
    id: "settings",
    heading: "Settings",
    actions: [
      {
        id: "preferences",
        label: "Preferences",
        description: "Customize your experience",
        icon: <Settings className="h-4 w-4" />,
        onSelect: () => go({ to: "/settings/general" }),
        keywords: ["settings", "preferences", "config"],
      },
      {
        id: "users",
        label: "Manage Users",
        description: "View and manage users",
        icon: <Users className="h-4 w-4" />,
        onSelect: () => go({ to: "/settings/members" }),
        keywords: ["users", "people", "team"],
      },
      {
        id: "help",
        label: "Help & Support",
        description: "Get help or contact support",
        icon: <HelpCircle className="h-4 w-4" />,
        onSelect: () => window.alert("Call cherry"),
        keywords: ["help", "support", "docs", "documentation"],
      },
    ],
  },
];

export function GlobalCommandBar() {
  const {
    isOpen,
    searchQuery,
    contextConfig,
    searchResults,
    setSearchQuery,
    closeCommandBar,
  } = useCommandBar();

  const { getList } = useDataProvider()();

  const go = useGo();

  // Set up search functionality
  useCommandBarSearch({
    searchFn: async (query) => {
      const result = await getList<IEventData>({
        resource: "events",
        filters: [],
        meta: { queryBy: "name", query },
      });
      return result.data.map((d) => d.document);
    },
    transformResult: (result) => ({
      id: result.id,
      label: result.name,
      description: `${result.type} in ${result.geodata.name} at ${moment(
        result.startDate * 1000,
      ).format("DD/MM/YYYY")}`,
      icon: (
        <ZoomablePhoto
          url={result.media?.photos?.[0]?.url}
          className="h-4 w-4"
        />
      ),
      onSelect: () => {
        go({
          to: `/events/edit/${result.id}`,
        });
        closeCommandBar();
      },
      keywords: [result.name, result.type, result.location],
    }),
    debounceMs: 500,
    minQueryLength: 5,
  });

  const shortcutsToFilter = contextConfig?.groups
    ? [...contextConfig.groups, ...defaultShortcuts({ go })]
    : defaultShortcuts({ go });

  // Filter default shortcuts based on search query
  const filteredShortcuts = shortcutsToFilter.map((group) => ({
    ...group,
    actions: group.actions.filter((action) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        action.label.toLowerCase().includes(query) ||
        action.description?.toLowerCase().includes(query) ||
        action.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(query),
        )
      );
    }),
  }));

  // Combine shortcuts: default shortcuts + context shortcuts
  const allShortcuts = filteredShortcuts;

  const hasResults =
    allShortcuts.some((group) => group.actions.length > 0) ||
    searchResults.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={closeCommandBar}>
      <div
        className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Command
          className="rounded-lg border bg-popover shadow-lg"
          shouldFilter={false}
        >
          {contextConfig?.badge && (
            <div className="px-4 pt-3 pb-2 border-b">
              <div className="inline-flex items-center gap-2 px-2 py-1 bg-accent rounded-md">
                <span className="text-xs font-medium">
                  {contextConfig.badge}
                </span>
              </div>
            </div>
          )}

          <Command.Input
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder={
              contextConfig?.label ||
              "Search for actions or type to search database..."
            }
            autoFocus
            className="flex h-12 w-full rounded-md bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />

          <Command.List className="max-h-[500px] overflow-y-auto p-2">
            {!hasResults && (
              <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                {searchQuery.length >= 2
                  ? "No results found."
                  : "Start typing to search..."}
              </Command.Empty>
            )}

            {allShortcuts.map(
              (group) =>
                group.actions.length > 0 && (
                  <Command.Group
                    key={group.id}
                    heading={group.heading}
                    className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:text-muted-foreground mb-2"
                  >
                    {group.actions.map((action) => (
                      <Command.Item
                        key={action.id}
                        onSelect={() => {
                          action.onSelect();
                          closeCommandBar();
                        }}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground hover:bg-accent/50 transition-colors",
                          action.destructive
                            ? "bg-destructive/20 text-destructive data-[selected=true]:bg-destructive/50 data-[selected=true]:text-destructive"
                            : "",
                        )}
                      >
                        {action.icon && (
                          <div className="mr-3 shrink-0">{action.icon}</div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium truncate">
                            {action.label}
                          </span>
                          {action.description && (
                            <span className="text-xs text-muted-foreground truncate">
                              {action.description}
                            </span>
                          )}
                        </div>
                      </Command.Item>
                    ))}
                  </Command.Group>
                ),
            )}

            {searchResults.length > 0 && (
              <Command.Group
                heading="Search Results"
                className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:flex **:[[cmdk-group-heading]]:items-center **:[[cmdk-group-heading]]:gap-2 mb-2"
              >
                {searchResults.map((result) => (
                  <Command.Item
                    key={result.id}
                    onSelect={() => {
                      result.onSelect();
                      closeCommandBar();
                    }}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground hover:bg-accent/50 transition-colors"
                  >
                    {result.icon && (
                      <div className="mr-3 shrink-0">{result.icon}</div>
                    )}
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">
                        {result.label}
                      </span>
                      {result.description && (
                        <span className="text-xs text-muted-foreground truncate">
                          {result.description}
                        </span>
                      )}
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className="border-t px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
                  ↑↓
                </kbd>{" "}
                Navigate
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
                  ↵
                </kbd>{" "}
                Select
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
                  ESC
                </kbd>{" "}
                Close
              </span>
            </div>
          </div>
        </Command>
      </div>
    </div>
  );
}
